import { useState, useRef, useCallback, useEffect, lazy, Suspense, type ReactNode } from 'react'
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom'
import { useAtlasAgent, API_URL } from './hooks/useAtlasAgent'
import { useNotifications } from './hooks/useNotifications'
import { NotificationProvider } from './context/NotificationContext'
import { CallProvider, useCallContext } from './context/CallContext'
import { useAuth } from './context/AuthContext'

function AdminOnly({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  if (user?.email !== 'admin@atlas.com') {
    return <Navigate to="/sessions" replace />
  }
  return children
}
import { Layout } from './components/Layout'
import { TopBar } from './components/TopBar'
import { ProtectedRoute } from './components/ProtectedRoute'
import { IncomingCallToast } from './components/IncomingCallToast'

// Route views are lazy-loaded so the initial bundle only contains the
// app shell — each view's chunk is fetched on first navigation.
const TaskList = lazy(() => import('./components/TaskList').then(m => ({ default: m.TaskList })))
const BoardView = lazy(() => import('./components/BoardView').then(m => ({ default: m.BoardView })))
const RunView = lazy(() => import('./components/RunView').then(m => ({ default: m.RunView })))
const ChannelInboxView = lazy(() => import('./components/ChannelInboxView').then(m => ({ default: m.ChannelInboxView })))
const EmailView = lazy(() => import('./components/EmailView').then(m => ({ default: m.EmailView })))
const WikiView = lazy(() => import('./components/WikiView').then(m => ({ default: m.WikiView })))
const NotesView = lazy(() => import('./components/NotesView').then(m => ({ default: m.NotesView })))
const HierarchyView = lazy(() => import('./components/HierarchyView').then(m => ({ default: m.HierarchyView })))
const SitesView = lazy(() => import('./components/SitesView').then(m => ({ default: m.SitesView })))
const SettingsView = lazy(() => import('./components/SettingsView').then(m => ({ default: m.SettingsView })))
const AgentPanel = lazy(() => import('./components/AgentPanel').then(m => ({ default: m.AgentPanel })))
const TaskDetailPanel = lazy(() => import('./components/TaskDetailPanel').then(m => ({ default: m.TaskDetailPanel })))
const ChatLayoutView = lazy(() => import('./components/ChatLayoutView').then(m => ({ default: m.ChatLayoutView })))
const DMView = lazy(() => import('./components/DMView').then(m => ({ default: m.DMView })))
const GroupChannelView = lazy(() => import('./components/GroupChannelView').then(m => ({ default: m.GroupChannelView })))
const WorkspaceView = lazy(() => import('./components/WorkspaceView').then(m => ({ default: m.WorkspaceView })))
const WhiteboardView = lazy(() => import('./components/WhiteboardView').then(m => ({ default: m.WhiteboardView })))
const CalendarView = lazy(() => import('./components/CalendarView').then(m => ({ default: m.CalendarView })))
const GanttView = lazy(() => import('./components/GanttView').then(m => ({ default: m.GanttView })))
const TableView = lazy(() => import('./components/TableView').then(m => ({ default: m.TableView })))
const LoginView = lazy(() => import('./components/LoginView').then(m => ({ default: m.LoginView })))
const CallView = lazy(() => import('./components/CallView').then(m => ({ default: m.CallView })))
import { useVersionCheck } from './hooks/useVersionCheck'
import './App.css'
import type { Task, WorkboardViewProps, AgentMessage, AgentWsMessage } from './types'

const TITLES: Record<string, string> = {
  home: 'Home',
  tasks: 'Tarefas',
  'agent-chat': 'Atlas Chat',
  runs: 'Execuções',
  memory: 'Memória',
  deployments: 'Deploys',
  logs: 'Logs',
  workboard: 'Workboard',
  messages: 'Mensagens',
  settings: 'Configurações',
  bms: 'Business Managers',
}

// Views que usam o TopBar de tarefas e o AgentPanel (apenas tasks e inbox)
const TASK_VIEWS = ['tasks', 'workboard', 'runs', 'agent-chat']

// Views que não usam o TopBar nem o AgentPanel
const AGENT_VIEWS = ['sessions', 'settings', 'chat', 'dm', 'workspace', 'messages', 'comments', 'home', 'emails', 'inbox-channels']

function generateId() {
  return Math.random().toString(36).slice(2, 9)
}

function nowTime() {
  return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function GlobalCallOverlay() {
  const call = useCallContext()
  const location = useLocation()
  const navigate = useNavigate()

  // Navigate to the call page in two cases:
  // - 'sfu-call-rejoin-navigate': auto-rejoin after page reload (replace,
  //   so the reload doesn't push a duplicate history entry).
  // - 'sfu-call-navigate': the user just answered a call (push, so the
  //   back button returns to wherever they were).
  useEffect(() => {
    const handler = (e: Event) => {
      const { isChannelCall, channelId, targetUserId, push } = (e as CustomEvent).detail || {}
      const opts = push ? undefined : { replace: true }
      if (isChannelCall && channelId) {
        navigate(`/channel/${channelId}`, opts)
      } else if (targetUserId) {
        navigate(`/dm/${targetUserId}`, opts)
      }
    }
    window.addEventListener('sfu-call-rejoin-navigate', handler)
    window.addEventListener('sfu-call-navigate', handler)
    return () => {
      window.removeEventListener('sfu-call-rejoin-navigate', handler)
      window.removeEventListener('sfu-call-navigate', handler)
    }
  }, [navigate])

  // Incoming call shows a compact toast pinned to the bottom of the
  // screen (caller avatar + accept/reject) instead of a full-screen
  // overlay — visible on any page without blocking navigation.
  if (call.incomingCall) return <IncomingCallToast call={call} />

  // No active call
  if (call.callState === 'idle' || call.callState === 'failed') return null

  // Determine if we're on the call's page (where CallView is embedded)
  const onCallPage = call.isChannelCall
    ? location.pathname.includes(`/channel/${call.channelId}`)
    : call.callTargetId && location.pathname.endsWith(call.callTargetId)

  if (onCallPage) return null

  // Not on the call page: render CallView hidden so the call stays alive.
  // If the user enabled PiP, the call content portals into the PiP window.
  return <Suspense fallback={null}><CallView call={call} onClose={() => {}} hidden /></Suspense>
}

function WorkboardRedirect({ board }: { board?: string }) {
  const navigate = useNavigate()
  const location = useLocation()
  useEffect(() => {
    const wsId = localStorage.getItem('atlas_ws_id')
    const boardId = board || sessionStorage.getItem('atlas_board_id') || 'default'
    const tab = sessionStorage.getItem('atlas_workboard_tab') || 'board'
    // Preserve the query string (e.g. ?task=<id>) so deep links keep working
    const qs = location.search || ''
    if (wsId) navigate(`/workboard/${wsId}/${boardId}/${tab}${qs}`, { replace: true })
    else navigate('/sessions', { replace: true })
  }, [navigate, location.search, board])
  return <div className="hermes-loading">Carregando workboard...</div>
}

function renderWorkboardView(tab: string, onTaskClick: WorkboardViewProps["onTaskClick"], onRunWithAgent: WorkboardViewProps["onRunWithAgent"], searchQuery?: string, assigneeFilter?: string) {
  const commonProps = { onTaskClick, searchQuery, assigneeFilter }
  switch (tab) {
    case 'board':
      return <BoardView {...commonProps} />
    case 'calendar':
      return <CalendarView {...commonProps} />
    case 'gantt':
      return <GanttView {...commonProps} />
    case 'table':
      return <TableView {...commonProps} />
    case 'runs':
      return <RunView />
    case 'list':
    default:
      return <TaskList {...commonProps} onRunWithAgent={onRunWithAgent} />
  }
}

function App() {
  const navigate = useNavigate()
  const location = useLocation()
  const activeView = (location.pathname.replace(/^\//, '').split('/')[0]) || 'sessions'
  const notifications = useNotifications()
  useVersionCheck()

  // Update document title with unread message count
  useEffect(() => {
    const count = notifications.unreadCount || 0
    document.title = count > 0 ? `(${count}) Atlas` : 'Atlas'
  }, [notifications.unreadCount])

  // Derive current view from URL path: /workboard/:wsId/:boardId/:view
  const pathParts = location.pathname.split('/')
  const validViews = ['list', 'board', 'calendar', 'gantt', 'table']
  // New format: /workboard/:wsId/:boardId/:view
  // Old format: /workboard/:wsId/:view
  let currentTab = sessionStorage.getItem('atlas_workboard_tab') || 'board'
  let currentBoardId = sessionStorage.getItem('atlas_board_id') || null
  if (pathParts[1] === 'workboard') {
    if (pathParts[4] && validViews.includes(pathParts[4])) {
      // New format: /workboard/:wsId/:boardId/:view
      currentTab = pathParts[4]
      currentBoardId = pathParts[3]
    } else if (pathParts[3] && validViews.includes(pathParts[3])) {
      // Old format: /workboard/:wsId/:view
      currentTab = pathParts[3]
    }
  }

  const setCurrentTab = (tab: string) => {
    sessionStorage.setItem('atlas_workboard_tab', tab)
    // Update URL if we're on a workboard route
    const match = location.pathname.match(/^\/workboard\/([0-9a-f-]{36})\/([0-9a-f-]{36})(\/.*)?$/)
    if (match) {
      navigate(`/workboard/${match[1]}/${match[2]}/${tab}`, { replace: true })
    } else {
      const matchOld = location.pathname.match(/^\/workboard\/([0-9a-f-]{36})(\/.*)?$/)
      if (matchOld) {
        navigate(`/workboard/${matchOld[1]}/${tab}`, { replace: true })
      } else if (location.pathname === '/tasks') {
        navigate(`/tasks/${tab}`, { replace: true })
      }
    }
  }
  const [agentOpen, setAgentOpen] = useState(false)
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const [taskDetailOpen, setTaskDetailOpen] = useState(false)
  const [messages, setMessages] = useState<AgentMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isPlayingAudio, setIsPlayingAudio] = useState(false)
  const [modelName, setModelName] = useState('')
  const [accessibilityData, setAccessibilityData] = useState<Record<string, unknown> | null>(null)
  const [locationData, setLocationData] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [assigneeFilter, setAssigneeFilter] = useState(() => localStorage.getItem('atlas_assignee_filter') || '')
  const currentAudioRef = useRef<HTMLAudioElement | null>(null)
  const stepIdCounter = useRef(0)
  const currentAgentMessageRef = useRef<AgentMessage | null>(null)

  const handleResponse = useCallback((data: AgentWsMessage) => {
    setIsLoading(false)

    if (data.text && data.text.includes('[[ACTIVATE_CAMERA]]')) {
      return
    }

    const currentMsg = currentAgentMessageRef.current
    const currentSteps = currentMsg?.steps
    if (currentMsg && currentSteps && currentSteps.length > 0) {
      setMessages(prev => prev.map(msg =>
        msg.id === currentMsg.id
          ? { ...msg, steps: [...(msg.steps || []), ...currentSteps] }
          : msg
      ))
    }

    setMessages(prev => [...prev, {
      id: generateId(),
      role: 'assistant',
      content: data.text || '',
      time: nowTime(),
      model: data.model,
      audio: data.audio,
      steps: [],
    }])

    currentAgentMessageRef.current = null

    if (data.audio) {
      if (currentAudioRef.current) currentAudioRef.current.pause()
      currentAudioRef.current = new Audio(`data:audio/mp3;base64,${data.audio}`)
      currentAudioRef.current.play()
      setIsPlayingAudio(true)
      currentAudioRef.current.onended = () => setIsPlayingAudio(false)
    }
  }, [])

  const handleStatus = useCallback((data: AgentWsMessage) => {
    const currentMsg = currentAgentMessageRef.current
    if (currentMsg) {
      setMessages(prev => prev.map(msg =>
        msg.id === currentMsg.id
          ? {
              ...msg,
              content: `${data.status || ''}: ${data.details || ''}`,
              steps: [
                ...(msg.steps || []),
                {
                  id: `step-${stepIdCounter.current++}`,
                  title: data.status || '',
                  details: data.details || '',
                  status: 'in_progress',
                  timestamp: nowTime(),
                }
              ]
            }
          : msg
      ))
    }
  }, [])

  const handleError = useCallback((data: AgentWsMessage) => {
    setIsLoading(false)
    setMessages(prev => [...prev, {
      id: generateId(),
      role: 'assistant',
      content: `Erro: ${data.error || 'Erro ao processar sua solicitação.'}`,
      time: nowTime(),
      steps: [],
    }])
    currentAgentMessageRef.current = null
  }, [])

  const { connectionStatus, modelStatus, sendPrompt } = useAtlasAgent({
    onResponse: handleResponse,
    onStatus: handleStatus,
    onError: handleError,
    onModel: (m?: string) => setModelName(m || ''),
  })

  const loadAccessibilityData = async () => {
    try {
      const response = await fetch(`${API_URL}/accessibility`, {
        headers: { 'Accept': 'application/json' }
      })
      const data = await response.json()
      setAccessibilityData(data)
    } catch (e: unknown) {
      console.error('Accessibility load error:', e)
    }
  }

  useEffect(() => {
    const interval = setInterval(loadAccessibilityData, 30000)
    loadAccessibilityData()
    return () => clearInterval(interval)
  }, [])

  const handleSendPrompt = useCallback(({ prompt, audioBase64, videoBase64, ttsProvider }: { prompt: string; audioBase64?: string; videoBase64?: string; ttsProvider?: string }) => {
    if (connectionStatus !== 'conectado') return

    const userMsg = {
      id: generateId(),
      role: 'user',
      content: prompt,
      time: nowTime(),
      steps: [],
    }

    const assistantMsg = {
      id: generateId(),
      role: 'assistant',
      content: 'Pensando...',
      time: nowTime(),
      steps: [],
    }

    setMessages(prev => [...prev, userMsg, assistantMsg])
    currentAgentMessageRef.current = assistantMsg
    setIsLoading(true)
    setAgentOpen(true)

    sendPrompt({
      prompt,
      audioBase64,
      videoBase64,
      location: locationData,
      ttsProvider,
    })
  }, [connectionStatus, locationData, sendPrompt])

  const handleTaskClick = (task: Task) => {
    setSelectedTaskId(task.id)
    setTaskDetailOpen(true)
    setAgentOpen(false)
  }

  const handleTaskUpdate = (updatedTask: Task | null) => {
    if (updatedTask === null) {
      setTaskDetailOpen(false)
      setSelectedTaskId(null)
      return
    }
    // Notify views (BoardView, TaskList, etc.) to update their local task state
    window.dispatchEvent(new CustomEvent('task-updated', { detail: updatedTask }))
  }

  const handleRunWithAgent = (task: Task) => {
    setAgentOpen(true)
    handleSendPrompt({
      prompt: `Execute a tarefa ${task.id}: "${task.title}". Comece analisando o contexto e depois execute as ações necessárias.`,
      ttsProvider: 'edge'
    })
  }

  const handleCreateTask = () => {
    const prompt = 'Crie uma nova tarefa no dashboard com título e prioridade. Pergunte qual tarefa devo criar.'
    setAgentOpen(true)
    handleSendPrompt({ prompt, ttsProvider: 'edge' })
  }

  const handleToggleAgent = () => setAgentOpen(prev => !prev)

  // Deep-link: /workboard/...?task=<id> opens the task detail panel
  // (used by task notification clicks and #task references in notes)
  useEffect(() => {
    const taskId = new URLSearchParams(location.search).get('task')
    if (taskId && location.pathname.startsWith('/workboard')) {
      setSelectedTaskId(taskId)
      setTaskDetailOpen(true)
      setAgentOpen(false)
    }
  }, [location.pathname, location.search])

  const showTopBar = TASK_VIEWS.includes(activeView)
  const showAgentPanel = agentOpen && TASK_VIEWS.includes(activeView)

  return (
    <Routes>
      <Route path="/login" element={<Suspense fallback={<div className="hermes-loading">Carregando...</div>}><LoginView /></Suspense>} />
      <Route path="*" element={
        <ProtectedRoute>
          <CallProvider>
          <NotificationProvider notifications={notifications}>
          <GlobalCallOverlay />
          <Layout
            activeView={activeView}
            onNavigate={navigate}
            unreadMessages={notifications.unreadCount}
            onMessagesRead={notifications.resetUnread}
            topBar={showTopBar ? (
              <TopBar
                title={TITLES[activeView] || 'Atlas'}
                currentView={currentTab}
                onChangeView={setCurrentTab}
                onCreateTask={handleCreateTask}
                onToggleAgent={handleToggleAgent}
                agentOpen={agentOpen}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                assigneeFilter={assigneeFilter}
                onAssigneeFilterChange={(v) => { setAssigneeFilter(v); localStorage.setItem('atlas_assignee_filter', v) }}
              />
            ) : null}
            agentPanel={
              taskDetailOpen ? (
                <Suspense fallback={null}>
                  <TaskDetailPanel
                    taskId={selectedTaskId}
                    isOpen={taskDetailOpen}
                    onClose={() => { setTaskDetailOpen(false); setSelectedTaskId(null) }}
                    onUpdate={handleTaskUpdate}
                  />
                </Suspense>
              ) : showAgentPanel ? (
                <Suspense fallback={null}>
                  <AgentPanel
                    isOpen={agentOpen}
                    onClose={() => setAgentOpen(false)}
                    connectionStatus={connectionStatus}
                    modelStatus={modelStatus || (modelName ? `Modelo: ${modelName}` : 'Modelo: -')}
                    onSendPrompt={handleSendPrompt}
                    messages={messages}
                    isLoading={isLoading}
                    isPlayingAudio={isPlayingAudio}
                    onCancel={() => { }}
                  />
                </Suspense>
              ) : null
            }
          >
            <Suspense fallback={<div className="hermes-loading">Carregando...</div>}>
            <Routes>
              <Route path="/" element={<WorkboardRedirect board="multi" />} />
              <Route path="/tasks" element={renderWorkboardView(currentTab, handleTaskClick, handleRunWithAgent, searchQuery, assigneeFilter)} />
              <Route path="/tasks/:view" element={renderWorkboardView(currentTab, handleTaskClick, handleRunWithAgent, searchQuery, assigneeFilter)} />
              <Route path="/workboard/:workspaceId" element={<WorkboardRedirect />} />
              <Route path="/workboard/:workspaceId/:view" element={renderWorkboardView(currentTab, handleTaskClick, handleRunWithAgent, searchQuery, assigneeFilter)} />
              <Route path="/workboard/:workspaceId/:boardId/:view" element={renderWorkboardView(currentTab, handleTaskClick, handleRunWithAgent, searchQuery, assigneeFilter)} />
              <Route path="/agent-chat" element={<TaskList onTaskClick={handleTaskClick} onRunWithAgent={handleRunWithAgent} searchQuery={searchQuery} assigneeFilter={assigneeFilter} />} />
              <Route path="/runs" element={<RunView />} />
              <Route path="/messages" element={<Navigate to="/" replace />} />
              <Route path="/inbox" element={<Navigate to="/workboard" replace />} />
              <Route path="/workboard" element={<WorkboardRedirect />} />
              <Route path="/inbox-channels" element={<ChannelInboxView />} />
              <Route path="/emails" element={<EmailView />} />
              <Route path="/wiki" element={<WikiView />} />
              <Route path="/notes" element={<NotesView />} />
              <Route path="/hierarchy" element={<HierarchyView />} />
              <Route path="/sites" element={<Navigate to="/bms" replace />} />
              <Route path="/bms" element={<SitesView />} />
              <Route path="/settings" element={<SettingsView />} />
              <Route path="/settings/skills" element={<Navigate to="/settings/agent-skills" replace />} />
              <Route path="/settings/:tab" element={<SettingsView />} />
              <Route path="/metrics" element={<Navigate to="/settings/metrics" replace />} />
              <Route path="/agent" element={<Navigate to="/settings/agent-download" replace />} />
              <Route path="/memory" element={<Navigate to="/settings/memory" replace />} />
              <Route path="/cron" element={<Navigate to="/settings/cron" replace />} />
              <Route path="/apikeys" element={<Navigate to="/settings/apikeys" replace />} />
              <Route path="/providers" element={<Navigate to="/settings/providers" replace />} />
              <Route path="/sessions" element={<ChatLayoutView />} />
              <Route path="/sessions/:sessionId" element={<ChatLayoutView />} />
              <Route path="/chat/:sessionId" element={<Navigate to="/sessions" replace />} />
              <Route path="/dm/:userId" element={<DMView />} />
              <Route path="/channel/:channelId" element={<GroupChannelView />} />
              <Route path="/workspace/:workspaceId" element={<WorkspaceView />} />
              <Route path="/whiteboard" element={<WhiteboardView />} />
              <Route path="*" element={<WorkboardRedirect board="multi" />} />
            </Routes>
            </Suspense>
          </Layout>
          </NotificationProvider>
          </CallProvider>
        </ProtectedRoute>
      } />
    </Routes>
  )
}

export default App
